import { z } from 'zod';
import { IsoDateStringSchema, UuidV7Schema } from './common.js';

/**
 * Code PIN de changement de vendeur sur tablette partagée.
 *
 * Modèle (docs/specs/pin-switch-vendeur.md §2) : **la tablette est
 * authentifiée, le vendeur est identifié**. Le PIN ne donne pas l'accès — la
 * session Clerk du superviseur l'a déjà ouvert — il désigne l'acteur à
 * l'intérieur de cette session. C'est ce qui rend 4 chiffres défendables :
 * ce n'est pas un mot de passe court, c'est une confirmation d'identité sur
 * une cible déjà désignée par un clic.
 */
export const PIN_LENGTH = 4;
export const PinSchema = z
  .string()
  .regex(new RegExp(`^\\d{${PIN_LENGTH}}$`), `Le code doit faire ${PIN_LENGTH} chiffres.`);

/** Échecs tolérés par (vendeur, tablette) avant verrouillage temporaire. */
export const PIN_MAX_FAILURES = 5;
/** Durée du verrouillage. Il renvoie vers le mot de passe, jamais vers un blocage sec (F8). */
export const PIN_LOCK_MINUTES = 15;

/**
 * En-tête porté par chaque requête d'une session PIN : l'id du vendeur au
 * nom duquel la tablette agit. Distinct de `X-Impersonate-As` (outil
 * d'administration, tracé comme tel) : ici il **réduit** les droits de la
 * session au périmètre d'un vendeur, il n'en ajoute jamais.
 */
export const ACTING_AS_HEADER = 'X-Acting-As';

/** Niveau d'authentification d'une requête, exposé sur `/users/me`. */
export const AuthLevelSchema = z.enum(['full', 'pin']);
export type AuthLevel = z.infer<typeof AuthLevelSchema>;

/** `POST /pin/me` — le vendeur pose (ou remplace) son propre PIN. */
export const SetPinSchema = z.object({ pin: PinSchema });
export type SetPinInput = z.infer<typeof SetPinSchema>;

/** `POST /pin/switch` — bascule vérifiée côté serveur (tablette en ligne). */
export const PinSwitchSchema = z.object({
  vendeurId: UuidV7Schema,
  pin: PinSchema,
});
export type PinSwitchInput = z.infer<typeof PinSwitchSchema>;

/**
 * `POST /pin/switch-logs` — déclaration d'une bascule vérifiée hors ligne
 * sur la tablette, rejouée par la file de synchronisation. Le PIN n'y figure
 * pas : il a été comparé sur l'appareil, on ne fait que dater l'événement.
 */
export const PinSwitchReportSchema = z.object({
  vendeurId: UuidV7Schema,
  switchedAt: IsoDateStringSchema,
});
export type PinSwitchReportInput = z.infer<typeof PinSwitchReportSchema>;

/**
 * `GET /pin/verifiers` — matériel de vérification hors ligne, mis en cache
 * sur la tablette (exigence N6 : la bascule doit marcher sans réseau).
 *
 * Un vérificateur de 4 chiffres est, par nature, équivalent au code pour qui
 * le détient (10 000 essais suffisent). On l'accepte parce que la tablette
 * qui le reçoit est **déjà** la session du superviseur, qui a autorité sur
 * ces vendeurs : le vérificateur n'ouvre rien que la session n'ouvre déjà.
 */
export const PinVerifierSchema = z.object({
  userId: UuidV7Schema,
  verifier: z.string().min(1),
});
export type PinVerifier = z.infer<typeof PinVerifierSchema>;

export const PinSwitchResultSchema = z.object({
  vendeur: z.object({
    id: UuidV7Schema,
    firstName: z.string(),
    lastName: z.string(),
  }),
});
export type PinSwitchResult = z.infer<typeof PinSwitchResultSchema>;

/** Codes d'erreur métier renvoyés par `POST /pin/switch` (corps `{ code, … }`). */
export const PIN_ERROR_CODES = {
  invalid: 'PIN_INVALID',
  locked: 'PIN_LOCKED',
  notSet: 'PIN_NOT_SET',
} as const;

/** Une ligne du classement du magasin (`GET /pin/leaderboard`). */
export const LeaderboardRowSchema = z.object({
  userId: UuidV7Schema,
  firstName: z.string(),
  lastName: z.string(),
  today: z.number().int().min(0),
  week: z.number().int().min(0),
  month: z.number().int().min(0),
});
export type LeaderboardRow = z.infer<typeof LeaderboardRowSchema>;
